import React, { useEffect, useRef, useState } from 'react'
import { CalendarIcon, CaretRightIcon } from '@okcredit/web-lib'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { IAppState } from 'infra/AppState'
import FontComponent from 'pages/CommonSummary/components/FontComponent'
import { FILTER_TYPE, ISupplyState } from 'state/new-summary/supply/supply.types'
import UnselectedIcon from 'assets/payments/unselected_checkbox.svg'
import SelectedIcon from 'assets/payments/selected_checkbox.svg'
import { listItemType } from 'pages/CommonSummary/components/ThreeDotsComponent/type'
import { NEWDASHBOARD_EVENTS } from 'state/new-summary/events/events.types'
import { pushEvent } from 'state/new-summary/events/events.actions'
import { fetchInnerSalesAnalyticsAction } from 'state/new-summary/supply/supply.actions'
import { convertDayToEpoc, convertDayToEpocStart } from 'utils/dateTransformer'
import { AnalyticsFilter } from 'pages/NewDashboard/components/Summary/components/SupplyContainer/SalesAnalyticsInner.types'

export const DEFAULT_SALES_PAGE_LIMIT = 100

const useSalesAnalyticsOptions = () => {
  const listRef = useRef<{ resetDialog: () => null }>({ resetDialog: () => null })
  const {
    SupplySummary: {
      statementGroupFilter,
      loaders: { isFetchingInnerSalesOrder },
      salesAnalyticsData: {
        paginationData: { page, per_page }
      }
    }
  } = useSelector((app: IAppState) => app.NewSummary)
  const dispatch = useDispatch()
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [selectedDayRange, setSelectedDayRange] = useState({
    from: null,
    to: null
  })

  const { filterType, endTime, startTime } = statementGroupFilter

  const fetchSalesAnalyticsInnerData = (payload: ISupplyState['statementGroupFilter']) => {
    dispatch(fetchInnerSalesAnalyticsAction(payload))
  }

  useEffect(() => {
    if (!!selectedDayRange.from && !!selectedDayRange.to) {
      setShowDatePicker(false)
      const payload = {
        startTime: convertDayToEpocStart(selectedDayRange.from),
        endTime: convertDayToEpoc(selectedDayRange.to),
        filterType: FILTER_TYPE.CUSTOM,
        page,
        per_page
      }
      fetchSalesAnalyticsInnerData(payload)

      listRef && listRef?.current?.resetDialog()
    }
  }, [selectedDayRange])

  const listItem: listItemType = [
    {
      icon: <CalendarIcon size={22} fill="black" />,
      title: (
        <FontComponent content="Today" contentType="name" fontColor="default" fontWeight={'bold'} />
      ),
      endAdornment: (
        <>
          {filterType === FILTER_TYPE.TODAY ? (
            <img src={SelectedIcon} alt=""></img>
          ) : (
            <img src={UnselectedIcon} alt="" />
          )}
        </>
      ),
      clickHandler: () => handleItemClick(FILTER_TYPE.TODAY)
    },
    {
      icon: <CalendarIcon size={22} fill="black" />,
      title: (
        <FontComponent
          content="Yesterday"
          contentType="name"
          fontColor="default"
          fontWeight={'bold'}
        />
      ),
      endAdornment: (
        <>
          {filterType === FILTER_TYPE.YESTERDAY ? (
            <img src={SelectedIcon} alt=""></img>
          ) : (
            <img src={UnselectedIcon} alt="" />
          )}
        </>
      ),
      clickHandler: () => handleItemClick(FILTER_TYPE.YESTERDAY)
    },
    {
      icon: <CalendarIcon size={22} fill="black" />,
      title: (
        <div style={{ display: 'flex' }}>
          <FontComponent
            content="Select Custom"
            contentType="name"
            fontColor="default"
            fontWeight={'bold'}
          />
        </div>
      ),
      endAdornment: <CaretRightIcon size={22} fill="black" />,
      clickHandler: () => handleItemClick(FILTER_TYPE.CUSTOM)
    }
  ]

  const closeCalenderAndList = () => {
    listRef?.current?.resetDialog()
    setShowDatePicker(false)
  }

  const getDateTitle = () => {
    if (filterType === FILTER_TYPE.CUSTOM) {
      return `Custom ( ${dayjs.unix(startTime).format('DD MMM YYYY')} - ${dayjs
        .unix(endTime)
        .format('DD MMM YYYY')} )`
    }

    return `${filterType} ( ${dayjs.unix(endTime).format('DD MMM YYYY')} )`
  }

  const getAnalyticsDateTitle = (filter: AnalyticsFilter, dateFilterType: FILTER_TYPE) => {
    if (dateFilterType === FILTER_TYPE.CUSTOM) {
      return `Custom ( ${dayjs.unix(filter.startTime).format('DD MMM YYYY')} - ${dayjs
        .unix(filter.endTime)
        .format('DD MMM YYYY')} )`
    }

    return `${dateFilterType} ( ${dayjs.unix(filter.endTime).format('DD MMM YYYY')} )`
  }

  const handleItemClick = (filter: FILTER_TYPE) => {
    if (filter === FILTER_TYPE.CUSTOM) {
      dispatch(pushEvent(NEWDASHBOARD_EVENTS.SUPPLY_SUMMARY_FILTER, { type: FILTER_TYPE.CUSTOM }))
      setShowDatePicker(true)
      return
    }

    const payload: any = {
      startTime: dayjs()
        .subtract(filter === FILTER_TYPE.YESTERDAY ? 1 : 0, 'day')
        .startOf('day')
        .unix(),
      endTime: dayjs()
        .subtract(filter === FILTER_TYPE.YESTERDAY ? 1 : 0, 'day')
        .endOf('day')
        .unix(),
      filterType: filter
    }
    fetchSalesAnalyticsInnerData(payload)
    dispatch(pushEvent(NEWDASHBOARD_EVENTS.SUPPLY_SUMMARY_FILTER, { type: filterType }))
    listRef && listRef?.current?.resetDialog()
    setShowDatePicker(false)
  }

  const handleDateChange = (value: any) => {
    setSelectedDayRange(value)
  }

  return {
    getDateTitle,
    listItem,
    listRef,
    showDatePicker,
    closeCalenderAndList,
    selectedDayRange,
    handleDateChange,
    isFetchingInnerSalesOrder,
    getAnalyticsDateTitle,
    statementGroupFilter
  }
}

export default useSalesAnalyticsOptions
